import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { useLang, LANGS } from "../i18n/LanguageContext";
import { Wordmark } from "./Logo";
import { scrollToId } from "./SmoothScroll";

export const NAV_IDS = ["about", "services", "logistics", "clients", "news", "contact"];

export const LangSwitcher = ({ testid = "lang-switcher", light = false }) => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button data-testid={testid} onClick={() => setOpen((o) => !o)}
        className={`mono text-[12px] uppercase flex items-center gap-1.5 px-3 py-2 rounded-full border transition-colors duration-300 ${light ? "border-[rgba(246,241,232,0.4)] text-[var(--bone)] hover:border-[var(--amber)]" : "border-[rgba(16,16,20,0.25)] text-[var(--ink)] hover:border-[var(--fux)]"}`}>
        {lang} <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-44 rounded-xl border border-[rgba(16,16,20,0.15)] bg-[rgba(251,249,244,0.97)] backdrop-blur-xl p-1.5 shadow-2xl z-50">
            {LANGS.map((l) => (
              <li key={l.code}>
                <button data-testid={`lang-option-${l.code}`} onClick={() => { setLang(l.code); setOpen(false); }}
                  className={`w-full flex justify-between items-center px-3 py-2 rounded-lg text-sm transition-colors duration-200 hover:bg-[var(--fux)] hover:text-white ${lang === l.code ? "text-[var(--fux)]" : "text-[var(--ink)]"}`}>
                  {l.label} <span className="mono text-[11px] uppercase">{l.code}</span>
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

const MobileMenu = ({ onClose }) => {
  const { t } = useLang();
  return (
    <motion.div data-testid="mobile-menu" initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} exit={{ clipPath: "inset(0 0 100% 0)" }}
      transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }} className="fixed inset-0 z-[60] bg-[rgba(251,249,244,0.98)] px-6 pt-24 pb-10 flex flex-col text-[var(--ink)]">
      <button data-testid="mobile-menu-close" onClick={onClose} className="absolute top-5 right-5 p-2 rounded-full border border-[rgba(16,16,20,0.25)]" aria-label={t.nav.close}><X className="w-5 h-5" /></button>
      <nav className="flex flex-col gap-2">
        {NAV_IDS.map((id, i) => (
          <motion.button key={id} data-testid={`mobile-nav-${id}`} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.06 }}
            onClick={() => { onClose(); setTimeout(() => scrollToId(id), 350); }} className="display text-3xl text-left py-2 border-b border-[rgba(16,16,20,0.15)]">
            {t.nav[id]}
          </motion.button>
        ))}
      </nav>
      <div className="mt-auto flex items-center justify-between"><LangSwitcher testid="mobile-lang-switcher" /><span className="mono text-[11px] opacity-70 uppercase">IT · EN · ES · PT · DE · FR · 中文 · AR</span></div>
    </motion.div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <>
      <motion.header data-testid="site-header" initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,color] duration-500 border-b ${scrolled ? "bg-[rgba(251,249,244,0.9)] backdrop-blur-xl border-[rgba(16,16,20,0.12)] text-[var(--ink)]" : "bg-transparent border-transparent text-[var(--bone)]"}`}>
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 h-[76px] flex items-center justify-between gap-6">
          <button data-testid="header-logo" onClick={() => scrollToId("top")} aria-label="MF Logistics Consulting Group"><Wordmark /></button>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_IDS.map((id) => (
              <button key={id} data-testid={`nav-${id}`} onClick={() => scrollToId(id)} className="link-sweep text-[14px] opacity-90 hover:opacity-100 transition-opacity">{t.nav[id]}</button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <LangSwitcher light={!scrolled} />
            <button data-testid="header-cta" onClick={() => scrollToId("contact")}
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-[var(--amber)] text-[var(--ink)] px-5 py-2.5 text-[13px] font-semibold hover:bg-[var(--fux)] hover:text-white transition-colors duration-300 group">
              {t.nav.cta}<ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <button data-testid="mobile-menu-open" onClick={() => setOpen(true)} className="lg:hidden p-2 rounded-full border border-[rgba(246,241,232,0.4)]" aria-label={t.nav.menu}><Menu className="w-5 h-5" /></button>
          </div>
        </div>
      </motion.header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
